import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import "./accessHistory.css";
import PrettyContainer from "../../prettyContainer/PrettyContainer";
import { Alert, Badge, Button, Spinner } from "react-bootstrap";
import { getLoginLogs } from "../../../helpers/api";
import moment from "moment";

const LIMIT = 25;

// Un solo intento del cliente puede generar varias peticiones seguidas (la
// aplicacion reintenta sola cuando le rebota). Se agrupan los intentos
// identicos y consecutivos que ocurren dentro de esta ventana de tiempo.
const GROUP_WINDOW_MS = 2 * 60 * 1000;

// Como se muestra cada resultado posible de un intento de login.
const RESULT_INFO = {
  SUCCESS: { label: "Signed in", variant: "success" },
  BAD_PASSWORD: { label: "Wrong password", variant: "warning" },
  DEVICE_LIMIT: { label: "Browser limit reached", variant: "danger" },
  USER_DISABLED: { label: "User disabled", variant: "secondary" },
  USER_NOT_FOUND: { label: "Unknown user", variant: "dark" },
};

export default function AccessHistoryPage() {
  const [searchParams] = useSearchParams();

  const [logs, setLogs] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState("");
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [filters, setFilters] = useState({
    // Permite llegar desde User Management ya filtrado por un cliente.
    username: searchParams.get("user") || "",
    result: "",
    days: "30",
  });

  useEffect(() => {
    setIsLoading(true);
    setError("");

    // Pequena espera para no lanzar una consulta por cada tecla.
    const timeoutId = setTimeout(() => {
      getLoginLogs({
        username: filters.username,
        result: filters.result,
        days: filters.days,
        page,
        limit: LIMIT,
      })
        .then((data) => {
          setLogs(data.logs || []);
          setTotal(data.total || 0);
          setTotalPages(data.totalPages || 1);
          setIsLoading(false);
        })
        .catch((err) => {
          setError(err || "Could not load the access history");
          setLogs([]);
          setIsLoading(false);
        });
    }, 350);

    return () => clearTimeout(timeoutId);
  }, [filters, page]);

  const handleFilter = (field) => (event) => {
    setPage(1);
    setFilters({ ...filters, [field]: event.target.value });
  };

  const renderResult = (result) => {
    const info = RESULT_INFO[result] || { label: result || "-", variant: "light" };
    return <Badge bg={info.variant}>{info.label}</Badge>;
  };

  // La lista llega del mas reciente al mas antiguo, asi que basta con comparar
  // cada registro con el grupo que se esta armando.
  const groupLogs = (list) => {
    const groups = [];

    list.forEach((log) => {
      const current = groups[groups.length - 1];
      const isRepeat =
        current &&
        current.username === log.username &&
        current.uuid === log.uuid &&
        current.result === log.result &&
        current.browser === log.browser &&
        current.browserVersion === log.browserVersion &&
        current.ip === log.ip &&
        new Date(current.oldest) - new Date(log.createdAt) <= GROUP_WINDOW_MS;

      if (isRepeat) {
        current.count += 1;
        current.oldest = log.createdAt;
      } else {
        groups.push({ ...log, count: 1, newest: log.createdAt, oldest: log.createdAt });
      }
    });

    return groups;
  };

  const grouped = groupLogs(logs);

  const renderRows = () =>
    grouped.map((log) => (
      <tr key={log._id}>
        <td>
          {moment(log.newest).format("YYYY-MM-DD HH:mm:ss")}
          {log.count > 1 ? (
            <>
              <Badge bg="secondary" className="ig-accessHistory-count">
                {`×${log.count}`}
              </Badge>
              <div className="ig-accessHistory-span">
                {`${log.count} attempts until ${moment(log.oldest).format("HH:mm:ss")}`}
              </div>
            </>
          ) : null}
        </td>
        <td>{log.username || "-"}</td>
        <td>
          {log.browser || "Unknown"}
          {log.browserVersion ? ` ${log.browserVersion}` : ""}
        </td>
        <td>{log.os || "Unknown"}</td>
        <td>{log.deviceType || "-"}</td>
        <td>
          {log.country || "-"}
          {log.city ? <span style={{ color: "#888" }}> ({log.city})</span> : null}
        </td>
        <td className="ig-accessHistory-ip">{log.ip || "-"}</td>
        <td>{renderResult(log.result)}</td>
        <td className="ig-accessHistory-device">
          {log.uuid ? log.uuid.slice(-8) : "-"}
          {log.isNewDevice ? (
            <Badge bg="info" style={{ marginLeft: "4px" }}>
              new
            </Badge>
          ) : null}
        </td>
      </tr>
    ));

  return (
    <PrettyContainer tittle="ACCESS HISTORY">
      <p className="ig-accessHistory-note">
        Every sign-in attempt is recorded here: which browser and operating system
        the customer used, the country the connection came from, and why the attempt
        was rejected. Identical attempts made within a couple of minutes are shown as
        a single row with a ×N counter, because one action by the customer can send
        several requests. Records older than one year are removed automatically.
      </p>

      <div className="ig-accessHistory-filters">
        <div>
          <label>Customer</label>
          <input
            type="text"
            placeholder="Username"
            value={filters.username}
            onChange={handleFilter("username")}
          />
        </div>
        <div>
          <label>Result</label>
          <select value={filters.result} onChange={handleFilter("result")}>
            <option value="">All</option>
            <option value="SUCCESS">Signed in</option>
            <option value="DEVICE_LIMIT">Browser limit reached</option>
            <option value="BAD_PASSWORD">Wrong password</option>
            <option value="USER_DISABLED">User disabled</option>
            <option value="USER_NOT_FOUND">Unknown user</option>
          </select>
        </div>
        <div>
          <label>Period</label>
          <select value={filters.days} onChange={handleFilter("days")}>
            <option value="1">Last 24 hours</option>
            <option value="7">Last 7 days</option>
            <option value="30">Last 30 days</option>
            <option value="90">Last 90 days</option>
            <option value="0">All</option>
          </select>
        </div>
        <div>
          <Button
            variant="outline-secondary"
            size="sm"
            onClick={() => setFilters({ ...filters })}
          >
            Refresh
          </Button>
        </div>
        <div style={{ marginLeft: "auto", color: "#666", fontSize: "13px" }}>
          {isLoading
            ? ""
            : grouped.length < logs.length
            ? `${total} attempts · ${grouped.length} rows on this page`
            : `${total} attempt${total === 1 ? "" : "s"}`}
        </div>
      </div>

      {error ? <Alert variant="danger">{error}</Alert> : null}

      <table className="ig-accessHistory-table">
        <thead>
          <tr>
            <th>Date</th>
            <th>Customer</th>
            <th>Browser</th>
            <th>Operating system</th>
            <th>Device</th>
            <th>Country</th>
            <th>IP</th>
            <th>Result</th>
            <th>Browser ID</th>
          </tr>
        </thead>
        <tbody>
          {isLoading ? (
            <tr>
              <td colSpan="9" style={{ textAlign: "center", padding: "40px" }}>
                <Spinner animation="border" role="status" variant="primary">
                  <span className="visually-hidden">Loading...</span>
                </Spinner>
                <p style={{ marginTop: "10px", color: "#666" }}>Loading access history...</p>
              </td>
            </tr>
          ) : logs.length === 0 ? (
            <tr>
              <td colSpan="9" style={{ textAlign: "center", padding: "40px", color: "#999" }}>
                No sign-in attempts found for this filter
              </td>
            </tr>
          ) : (
            renderRows()
          )}
        </tbody>
      </table>

      {!isLoading && totalPages > 1 && (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: "8px",
            marginTop: "16px",
            flexWrap: "wrap",
          }}
        >
          <Button
            variant="outline-secondary"
            size="sm"
            onClick={() => setPage(1)}
            disabled={page <= 1}
          >
            «
          </Button>
          <Button
            variant="outline-secondary"
            size="sm"
            onClick={() => setPage(page - 1)}
            disabled={page <= 1}
          >
            ‹ Prev
          </Button>
          <span style={{ fontSize: "14px", color: "#555" }}>
            Page {page} of {totalPages}
          </span>
          <Button
            variant="outline-secondary"
            size="sm"
            onClick={() => setPage(page + 1)}
            disabled={page >= totalPages}
          >
            Next ›
          </Button>
          <Button
            variant="outline-secondary"
            size="sm"
            onClick={() => setPage(totalPages)}
            disabled={page >= totalPages}
          >
            »
          </Button>
        </div>
      )}
    </PrettyContainer>
  );
}
